import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { STYLE_MASQUE_VISUELLEMENT } from '../../../../../packages/ui/src/composants/styles.js';
import { useBoiteModale } from '../../../../../packages/ui/src/composants/useBoiteModale.js';
import { cheminEcran } from '../../app/ecrans.js';
import { CIBLE_MIN, COULEURS, POLICE_SYSTEME } from '../../app/styles.js';
import { ETATS_ANNONCES, MODALE_RENONCIATION, TEXTES } from './libelles.js';
import {
  etapeCourante,
  resumeProgression,
  formaterInstant,
  type ActionUnique,
  type EtapeFrise,
  type MembreFamille,
  type VuePaiement,
} from './modele.js';

/** Portails client : police système, 18 px au moins, cibles de 44 px au moins, aucune police chargée (SYNTHESE §2, UX-20, UX-21). */
export const styleRacine: CSSProperties = {
  fontFamily: POLICE_SYSTEME,
  fontSize: 18,
  lineHeight: 1.5,
  color: COULEURS.marineFonce,
  maxWidth: '70ch',
  marginInline: 'auto',
  paddingInline: 16,
  boxSizing: 'border-box',
};
const styleTitre1: CSSProperties = { color: COULEURS.marine, fontSize: 28, lineHeight: 1.25, marginBlock: '16px 8px' };
const styleTitre2: CSSProperties = { color: COULEURS.marine, fontSize: 22, lineHeight: 1.3, marginBlock: '0 8px' };
const styleSectionBloc: CSSProperties = { marginBlock: 24 };
const styleLienBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: CIBLE_MIN,
  minWidth: CIBLE_MIN,
  padding: '0 16px',
  boxSizing: 'border-box',
  borderRadius: 8,
  font: 'inherit',
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
};
export const styleSecondaire: CSSProperties = {
  ...styleLienBase,
  border: `2px solid ${COULEURS.marine}`,
  background: COULEURS.blanc,
  color: COULEURS.marine,
};
export const stylePrincipal: CSSProperties = {
  ...styleLienBase,
  border: '2px solid transparent',
  background: COULEURS.boutonPrincipal,
  color: COULEURS.blanc,
};
const styleRangee: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, marginBlock: 12 };
export const styleTexteSecondaire: CSSProperties = { color: COULEURS.ardoise, margin: 0 };

/** Section nommée par son titre : un repère pour le lecteur d'écran (navigation par régions). */
export function Bloc({ titre, enfants, style }: { titre: string; enfants: ReactNode; style?: CSSProperties }) {
  const id = useId();
  return (
    <section aria-labelledby={id} style={{ ...styleSectionBloc, ...style }}>
      <h2 id={id} style={styleTitre2}>
        {titre}
      </h2>
      {enfants}
    </section>
  );
}

export function TitreEcran({ titre, salutation }: { titre: string; salutation?: string }) {
  return (
    <>
      <h1 style={styleTitre1}>{titre}</h1>
      {salutation ? <p style={{ marginBlock: 0 }}>{salutation}</p> : null}
    </>
  );
}

/** Frise horizontale (« fresque », #1) : étapes enchaînées par un trait de liaison, défilement horizontal, état dit en texte. */
export function FriseHorizontale({ etapes }: { etapes: readonly EtapeFrise[] }) {
  const courante = etapeCourante(etapes);
  const liste = useRef<HTMLOListElement>(null);
  // L'étape en cours doit être visible sans défiler à la main : on ne fait défiler que la frise, jamais la page.
  useEffect(() => {
    const cible = liste.current?.querySelector<HTMLElement>('[aria-current="step"]');
    if (liste.current && cible && typeof cible.scrollIntoView === 'function') cible.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [etapes]);
  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <p style={{ marginBlock: '0 8px', fontWeight: 600, color: COULEURS.marine }}>{resumeProgression(etapes)}</p>
      <ol
        ref={liste}
        aria-label={TEXTES.progression}
        data-orientation="horizontale"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: '0 0 8px',
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          gap: 0,
          position: 'relative',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {etapes.map((etape, i) => (
          <li
            key={`${i}-${etape.code}`}
            aria-current={etape.etat === 'courant' ? 'step' : undefined}
            style={{ flex: '0 0 auto', minWidth: 132, maxWidth: 160, paddingRight: 8, fontWeight: etape.etat === 'courant' ? 700 : 400 }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  border: `2px solid ${COULEURS.marine}`,
                  background: etape.etat === 'a_venir' ? COULEURS.blanc : COULEURS.marine,
                  color: etape.etat === 'a_venir' ? COULEURS.marine : COULEURS.blanc,
                  outline: etape.etat === 'courant' ? `3px solid ${COULEURS.boutonPrincipal}` : 'none',
                  outlineOffset: 2,
                }}
              >
                {etape.etat === 'fait' ? '✓' : i + 1}
              </span>
              {i < etapes.length - 1 ? (
                <span
                  aria-hidden="true"
                  style={{ flex: 1, height: 2, background: etape.etat === 'fait' ? COULEURS.marine : COULEURS.bordure, marginInline: 4 }}
                />
              ) : null}
            </div>
            <span style={{ display: 'block', marginTop: 4 }}>
              {etape.libelle}
              <span style={STYLE_MASQUE_VISUELLEMENT}>{`, ${ETATS_ANNONCES[etape.etat]}`}</span>
            </span>
          </li>
        ))}
      </ol>
      {courante?.detail ? (
        <p style={{ marginBlock: '8px 0' }}>{`${courante.detail.charAt(0).toUpperCase()}${courante.detail.slice(1)}.`}</p>
      ) : null}
    </div>
  );
}

export function LienVoirMonArbre() {
  return (
    <p style={{ marginBlock: '12px 0' }}>
      <a href={cheminEcran('X15')} style={styleSecondaire}>
        {TEXTES.voirArbre}
      </a>
    </p>
  );
}

/** UNE seule chose à faire : un bouton principal et, au plus, son alternative. Jamais la liste complète. */
export function BlocActionUnique({ action, onAlternative }: { action: ActionUnique; onAlternative?: () => void }) {
  return (
    <Bloc
      titre={TEXTES.actionUnique}
      enfants={
        <>
          <p style={{ marginBlock: '0 8px' }}>{action.texte}</p>
          {action.ecranCible && action.libelleBouton ? (
            <div style={styleRangee}>
              <a href={action.lien ?? cheminEcran(action.ecranCible)} data-action="principale" style={stylePrincipal}>
                {action.libelleBouton}
              </a>
              {action.libelleAlternative ? (
                <a
                  href={action.lien ?? cheminEcran(action.ecranCible)}
                  onClick={(e) => {
                    if (!onAlternative) return;
                    e.preventDefault();
                    onAlternative();
                  }}
                  style={styleSecondaire}
                >
                  {action.libelleAlternative}
                </a>
              ) : null}
            </div>
          ) : null}
        </>
      }
    />
  );
}

export function BlocFamille({ famille, actions }: { famille: readonly MembreFamille[]; actions: ReactNode }) {
  return (
    <Bloc
      titre={TEXTES.famille}
      enfants={
        <>
          {famille.length === 0 ? (
            <p>{TEXTES.familleVide}</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {famille.map((m) => (
                <li key={m.id} style={{ paddingBlock: 8, borderBottom: `1px solid ${COULEURS.bordure}` }}>
                  <strong>{m.nom}</strong>
                  {m.lien ? `, ${m.lien}` : ''}
                  {m.vivant ? '' : ` (${TEXTES.decede})`}
                  {m.hypothese ? <em>{` (${TEXTES.aConfirmer})`}</em> : null}
                  {m.contacts.map((c) => (
                    <span key={`${c.type}-${c.value}`} style={{ display: 'block', color: COULEURS.ardoise }}>
                      {c.value}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}
          <p style={styleTexteSecondaire}>{TEXTES.familleExplication}</p>
          {actions}
        </>
      }
    />
  );
}

export function BlocPaiement({ paiement }: { paiement: VuePaiement }) {
  return (
    <Bloc
      titre={TEXTES.participation}
      enfants={
        <>
          {paiement.genre === 'a_jour' ? (
            <>
              <p style={{ marginBlock: '0 4px', fontWeight: 700, color: COULEURS.marine }}>{TEXTES.aJour}</p>
              <p style={{ marginBlock: 0 }}>{TEXTES.aJourExplication}</p>
            </>
          ) : (
            <>
              <p style={{ marginBlock: '0 4px' }}>
                {`${TEXTES.resteDu} : `}
                <strong>{paiement.resteDu}</strong>
              </p>
              {paiement.prochaine && paiement.echeance ? (
                <p style={{ marginBlock: 0 }}>{`${TEXTES.prochaineEcheance} : ${paiement.prochaine}, avant le ${paiement.echeance}.`}</p>
              ) : null}
            </>
          )}
          <p style={{ ...styleTexteSecondaire, marginBlock: '8px 0' }}>{TEXTES.partNotaire}</p>
          <p style={{ marginBlock: '12px 0' }}>
            <a href={paiement.urlRecus} style={styleSecondaire}>
              {TEXTES.voirRecus}
            </a>
          </p>
        </>
      }
    />
  );
}

export function BlocEchanges({
  etat,
}: {
  etat: { statut: 'chargement' } | { statut: 'ok'; entrees: { at: string; summary: string }[] } | { statut: 'erreur' };
}) {
  return (
    <Bloc
      titre={TEXTES.echanges}
      enfants={
        etat.statut === 'chargement' ? (
          <p style={styleTexteSecondaire}>{TEXTES.chargement}</p>
        ) : etat.statut === 'erreur' ? (
          <p role="alert">{TEXTES.echangesPanne}</p>
        ) : etat.entrees.length === 0 ? (
          <p>{TEXTES.echangesVide}</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {etat.entrees.map((e, i) => (
              <li key={`${i}-${e.at}`} style={{ paddingBlock: 4 }}>
                {`${formaterInstant(e.at)} : ${e.summary}`}
              </li>
            ))}
          </ul>
        )
      }
    />
  );
}

/** Renonciation (#26) : la compensation en foncier est proposée avant ; la renonciation n'est pas présentée comme la meilleure solution. */
export function ModaleRenonciation({ onFermer }: { onFermer: () => void }) {
  const boite = useRef<HTMLDivElement>(null);
  const idTitre = useId();
  const surClavier = useBoiteModale(boite, { onEchap: onFermer });
  const t = MODALE_RENONCIATION;
  return (
    <div
      ref={boite}
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitre}
      tabIndex={-1}
      onKeyDown={surClavier}
      style={{
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        background: COULEURS.blanc,
        padding: 16,
        fontFamily: POLICE_SYSTEME,
        fontSize: 18,
        lineHeight: 1.5,
        color: COULEURS.marineFonce,
        zIndex: 10,
      }}
    >
      <div style={{ maxWidth: '70ch', marginInline: 'auto' }}>
        <h2 id={idTitre} style={styleTitre2}>
          {t.titre}
        </h2>
        <h3 style={{ fontSize: 19, color: COULEURS.marine }}>{t.compensationTitre}</h3>
        <p>{t.compensationTexte}</p>
        <h3 style={{ fontSize: 19, color: COULEURS.marine }}>{t.renonciationTitre}</h3>
        <p>{t.renonciationTexte}</p>
        <p>{t.contact}</p>
        <button type="button" onClick={onFermer} style={{ ...stylePrincipal, border: '2px solid transparent' }}>
          {t.fermer}
        </button>
      </div>
    </div>
  );
}

export { styleRangee };

export interface VideoPortail {
  id: string;
  title: string;
  embedUrl: string;
}

/**
 * Vidéos que le cabinet a partagées avec la famille (PARITE-5) : titre et lecteur seulement (youtube-nocookie.com). Rien à montrer :
 * aucun bloc.
 */
export function BlocVideos({ videos }: { videos: readonly VideoPortail[] }) {
  if (videos.length === 0) return null;
  return (
    <section aria-label="Vidéos du cabinet" style={styleSectionBloc}>
      <h2 style={styleTitre2}>Vidéos du cabinet</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {videos.map((v) => (
          <div key={v.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong style={{ overflowWrap: 'anywhere' }}>{v.title}</strong>
            <div
              style={{ position: 'relative', width: '100%', paddingTop: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden' }}
            >
              <iframe
                src={v.embedUrl}
                title={`Vidéo : ${v.title}`}
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
